/**
 * livretAutoEvalMap.js
 *
 * Mapping entre les exercices auto-évalués sur la trame Plateau
 * (P1/P2/P3, cf. `data/plateauProgression.js`) et les 12 lignes
 * « Ma progression » du chapitre 06 du Livret Plateau interactif.
 *
 * Objectif : quand l'élève a rempli son auto-éval (✅ 🔁 ⏳) sur la
 * trame, il peut pré-remplir automatiquement son chapitre 06 sans
 * ressaisir chaque compétence à la main.
 *
 * Aucun endpoint dédié : lecture directe des clés localStorage
 * `code2roues:autoeval-plateau-<phaseId>:v1` puis merge côté client.
 *
 * Choix produit : la donnée trame reste la source de vérité de la
 * séance ; le livret reste éditable manuellement — on ne fait que
 * PROPOSER une pré-remplissage. L'élève choisit Fusionner / Remplacer.
 */

/**
 * Mapping exerciceId (trame) → indexProgression (livret ch06).
 * Plusieurs exercices peuvent pointer vers la même compétence livret,
 * dans ce cas on garde la meilleure évaluation (acquis > encours > decouvert).
 * Les compétences du livret non listées ici (regard, préparation examen)
 * restent en édition manuelle uniquement.
 */
export const PLATEAU_AUTOEVAL_TO_PROG = {
  // prog-0 : Regard — pas d'exercice direct sur la trame plateau
  //          (transversal à tous). Édition manuelle.

  // prog-1 : Position de conduite
  'position': 1,

  // prog-2 : Manipulation à l'arrêt
  'demarrage-arret': 2,
  'deplacer-moteur-coupe': 2,

  // prog-3 : Commandes moto
  'rapports': 3,
  'braquage': 3,
  'arret-depart-tournant': 3,

  // prog-4 : Point de patinage
  'patinage': 4,

  // prog-5 : Allure lente
  'lent-14': 5,
  'demi-tour': 5,
  'demi-tour-arrete': 5,
  'demi-tour-lance': 5,

  // prog-6 : Freinage d'urgence
  'freinage-urgence': 6,
  'repartition-freinage': 6,

  // prog-7 : Passager
  'demi-tour-passager': 7,

  // prog-8 : Slalom
  'contre-braquage-slalom': 8,

  // prog-9 : Évitement
  'evitement': 9,

  // prog-10 : Enchaînement complet
  'assemblage': 10,

  // prog-11 : Préparation examen — édition manuelle
};

/** Ordre de qualité pour agréger plusieurs exercices sur une même ligne. */
const STATUS_RANK = { acquis: 3, encours: 2, decouvert: 1, none: 0 };

/** Convertit un état auto-éval trame vers un statut livret. */
export function autoEvalToLivretStatus(v) {
  if (v === 'ok')   return 'acquis';
  if (v === 'todo') return 'encours';
  if (v === 'no')   return 'decouvert';
  return null;
}

/**
 * Charge toutes les auto-évaluations Plateau depuis localStorage.
 * @returns {Object} { exerciseId: 'ok'|'todo'|'no' }
 */
export function loadPlateauAutoEval() {
  const merged = {};
  if (typeof window === 'undefined') return merged;
  const prefix = 'code2roues:autoeval-plateau-';
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(prefix) || !key.endsWith(':v1')) continue;
      try {
        const raw = JSON.parse(localStorage.getItem(key) || '{}');
        Object.entries(raw).forEach(([exId, val]) => {
          if (val === 'ok' || val === 'todo' || val === 'no') {
            merged[exId] = val;
          }
        });
      } catch (_) { /* skip corrupted */ }
    }
  } catch (_) { /* localStorage inaccessible */ }
  return merged;
}

/**
 * Calcule un dict { 'prog-N': 'acquis'|'encours'|'decouvert' } à partir
 * d'un dict d'auto-éval Plateau. Si plusieurs exercices pointent vers
 * la même ligne livret, on garde la meilleure évaluation.
 */
export function computeProgFromAutoEval(autoEvalDict) {
  const result = {};
  Object.entries(autoEvalDict || {}).forEach(([exId, val]) => {
    const idx = PLATEAU_AUTOEVAL_TO_PROG[exId];
    if (idx === undefined) return;
    const livretStatus = autoEvalToLivretStatus(val);
    if (!livretStatus) return;
    const key = `prog-${idx}`;
    const prev = result[key];
    if (!prev || (STATUS_RANK[livretStatus] || 0) > (STATUS_RANK[prev] || 0)) {
      result[key] = livretStatus;
    }
  });
  return result;
}

/**
 * Résumé statistique — utilisé dans le banner de proposition
 * (« 8 compétences détectées, dont 3 acquises, 4 en cours, 1 découvertes »).
 */
export function summarizeProgUpdates(progUpdates) {
  const values = Object.values(progUpdates || {});
  return {
    total: values.length,
    acquis: values.filter(v => v === 'acquis').length,
    encours: values.filter(v => v === 'encours').length,
    decouvert: values.filter(v => v === 'decouvert').length,
  };
}
